import { useMemo, useRef, useState } from "react"
import { MenuItem } from "@mui/material"
import { MRT_ColumnDef, MRT_TableInstance } from "material-react-table"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import {
    fetchSubscriptionsForSupplier,
    SubscriptionsForSupplierResponse,
    SupplierSubscriptionQuery,
} from "../subscriptionsApi"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { SUPPLIER_REPORTS_ROUTE } from "../../../../../../constants/routes"
import { AmountUnit } from "../../../../../currency/currencyTypes"
import { useSupplier } from "../../../../supplierHooks"
import { SubscriptionOrder } from "../../../../../company/reports/accounting/subscriptions/subscriptionsTypes"
import { SelectMerchantAutocomplete } from "../../components/inputFilters/SelectMerchantAutocomplete"
import {
    defaultSubscriptionsFilterOptionsList,
    FiltersDrawerSubscriptions,
    SubscriptionsFilterProps,
} from "../components/FiltersDrawersSubscriptions"
import useActiveFiltersCount from "../hooks/useActiveFiltersCount"
import { StandardExportButton } from "../../../../../../shared/components/table/StandardExportButton"
import { useExportSubscriptions } from "../hooks/useExportSupplierSubscriptions"
import { DateTime } from "luxon"
import { StandardTwoLinesCell } from "../../../../../../shared/components/table/StandardTwoLinesCell"
import { StandardFilterButton } from "../../../../../../shared/components/table/StandardFilterButton"
import { StandardTableFilter } from "../../../../../../shared/components/table/StandardTableFilters"
import { useInitialDateFilters } from "../../../../../../shared/components/table/useInitialDateFilters"
import { StandardDateRangeFilter } from "../../../../../../shared/components/table/StandardDateRangeFilter"

const selectSubscriptions = (data: SubscriptionsForSupplierResponse) => data?.orders || []

export function SupplierSubscriptions() {
    const { l10n } = useAppLocalization()

    const { supplierId } = useSupplier()

    const [drawerOpen, setDrawerOpen] = useState(false)

    const { startDate, endDate } = useInitialDateFilters()
    const [filtersList, setFiltersList] = useState<SubscriptionsFilterProps>({
        ...defaultSubscriptionsFilterOptionsList,
        startDate,
        endDate,
    })
    const { activeCount } = useActiveFiltersCount(filtersList)

    // An order id identifies the order uniquely, so the search must not be narrowed by the
    // period - the user searches by id precisely because the date is unknown.
    const isSearchingByOrderId = Boolean(filtersList.orderId)

    const subscriptionsResult = useApiQuery(fetchSubscriptionsForSupplier, {
        queryName: SupplierSubscriptionQuery.SUBSCRIPTIONS_FOR_SUPPLIER_REPORTS,
        dependencies: {
            supplierId,
            ...filtersList,
            merchantIds: filtersList.merchantIds?.join(","),
            shopIds: filtersList.shopIds?.join(","),
            subscriptionIds: filtersList.subscriptionIds?.join(","),
            paymentMethods: filtersList.paymentMethods?.join(","),
            startDate: isSearchingByOrderId ? undefined : filtersList.startDate?.toISODate(),
            endDate: isSearchingByOrderId ? undefined : filtersList.endDate?.toISODate(),
        },
        select: selectSubscriptions,
    })

    const tableInstanceRef = useRef<MRT_TableInstance<SubscriptionOrder>>(null)

    const { ApiQueryTable, columnFactory } = useApiQueryTable(subscriptionsResult)

    const columns = useMemo<MRT_ColumnDef<SubscriptionOrder>[]>(
        () => [
            columnFactory.idColumn({
                header: l10n.getString("supplier-subscriptions-table-order-id-column"),
                accessorKey: "id",
            }),
            columnFactory.nameColumn({
                header: l10n.getString("supplier-subscriptions-table-employee-column"),
                accessorKey: "customer.displayName",
                Cell: ({ row }) => (
                    <StandardTwoLinesCell
                        title={row.original.customer.displayName}
                        subtitle1={row.original.customer.employeeId}
                        subtitle2={row.original.customer.email}
                    />
                ),
            }),
            columnFactory.dateColumn(
                {
                    header: l10n.getString("supplier-subscriptions-table-subscribed-column"),
                    accessorKey: "subscriptionDetails.validFrom",
                },
                (row) => row.subscriptionDetails.validFrom,
            ),
            columnFactory.dateColumn(
                {
                    header: l10n.getString("supplier-subscriptions-table-unsubscribed-column"),
                    accessorKey: "subscriptionDetails.validTo",
                },
                (row) => row.subscriptionDetails.validTo,
            ),
            columnFactory.shortNameColumn({
                header: l10n.getString("supplier-subscriptions-table-payment-column"),
                id: "paymentDetails.paymentMethod",
                accessorFn: (row) => {
                    const paymentMethod = row.paymentDetails?.paymentMehod

                    if (paymentMethod) {
                        return l10n.getStringForEnum("purchases-payment-method", paymentMethod, paymentMethod)
                    }

                    return ""
                },
                Cell: ({ cell }) => {
                    return <p style={{ inlineSize: "8rem", overflowWrap: "break-word" }}>{cell.getValue<string>()}</p>
                },
            }),
            columnFactory.moneyColumnWithUnit(
                {
                    header: l10n.getString("supplier-subscriptions-table-amount-column"),
                    id: "amount",
                },
                (row) => row.amount,
                AmountUnit.MONTH,
            ),
        ],
        [l10n, columnFactory],
    )

    const { handleExportRows } = useExportSubscriptions(
        filtersList.startDate?.toISODate() || DateTime.now().toISODate(),
        filtersList.endDate?.toISODate() || DateTime.now().toISODate(),
    )

    return (
        <>
            <LocalizedStrict id="supplier-subscriptions-header" attrs={{ title: true }}>
                <PageHeader page={Page.SUPPLIER_SUBSCRIPTIONS} title="Subscriptions">
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="supplier-nav-item-home">
                            <BreadcrumbLink to={SUPPLIER_REPORTS_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="supplier-nav-item-accounting">
                            <Breadcrumb>Accounting</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="supplier-nav-item-subscriptions">
                            <Breadcrumb>Subscriptions</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <ApiQueryTable
                columns={columns}
                tableInstanceRef={tableInstanceRef}
                initialState={{ density: "spacious" }}
                renderRowActionMenuItems={({ row, closeMenu }) => [
                    <LocalizedStrict id="supplier-subscriptions-table-cancel-subscription">
                        <MenuItem
                            onClick={() => {
                                closeMenu()
                            }}
                            key={`cancel-subscription-${row.original.id}`}
                        >
                            Cancel subscription
                        </MenuItem>
                    </LocalizedStrict>,
                    <LocalizedStrict id="supplier-subscriptions-table-details">
                        <MenuItem
                            onClick={() => {
                                closeMenu()
                            }}
                            key={`details-${row.original.id}`}
                        >
                            Details
                        </MenuItem>
                    </LocalizedStrict>,
                ]}
                renderTopToolbarCustomActions={() => (
                    <StandardTableFilter variant="left">
                        <StandardDateRangeFilter
                            value={{
                                startDate: filtersList.startDate,
                                endDate: filtersList.endDate,
                            }}
                            onChange={(newValue) => {
                                setFiltersList((prev) => ({
                                    ...prev,
                                    startDate: newValue.startDate,
                                    endDate: newValue.endDate,
                                }))
                            }}
                        />
                        <SelectMerchantAutocomplete
                            merchantId={filtersList.merchantId}
                            setMerchantId={(merchantId) =>
                                setFiltersList((prev) => ({
                                    ...prev,
                                    merchantId,
                                }))
                            }
                        />
                    </StandardTableFilter>
                )}
                renderToolbarInternalActions={({ table }) => (
                    <StandardTableFilter variant="right" table={table}>
                        <StandardFilterButton onClick={() => setDrawerOpen(true)} activeCount={activeCount} />
                        <StandardExportButton
                            onClick={() => {
                                handleExportRows(tableInstanceRef.current?.getRowModel().rows)
                            }}
                        />
                    </StandardTableFilter>
                )}
            />

            <FiltersDrawerSubscriptions
                setFiltersList={setFiltersList}
                drawerOpen={drawerOpen}
                setDrawerOpen={setDrawerOpen}
                filtersList={filtersList}
            />
        </>
    )
}
